import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { getWalletTransactionList } from "services/rewardsServices";
import {
  EMPTY_ANALYTICS_DASHBOARD,
  getWalletTransactionListPayload,
  mapWalletTransactionAnalyticsResponse,
} from "./rewardsAnalyticsMappers";
import { hasModuleAction } from "./rewardsUtils";
import "./RewardsAnalytics.css";

const toCsvSafe = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
const inr = (coins, coinValue) =>
  `₹${(Number(coins || 0) * Number(coinValue || 0)).toLocaleString("en-IN")}`;
const getUserTotalEarned = (user) =>
  Number(user?.rewardsCoinsEarned || 0) + Number(user?.referralCoinsEarned || 0);

function mapUserForPopup(user, coinsEarned) {
  return {
    id: user.id,
    name: user.name,
    phone: user.phone,
    profession: user.profession,
    qualification: user.qualification || "",
    specialisation: user.specialisation,
    city: user.city,
    coinsEarned: coinsEarned ?? getUserTotalEarned(user),
  };
}

const getAvailableCoins = (user) =>
  getUserTotalEarned(user) -
  Number(user?.coinsRedeemed || 0) -
  Number(user?.coinsExpired || 0) -
  Number(user?.coinsReversed || 0);

function formatDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDefaultDateRange() {
  const endDate = new Date();
  const startDate = new Date(endDate.getFullYear(), 7, 1);
  return {
    startDate: formatDateInput(startDate),
    endDate: formatDateInput(endDate),
  };
}

const TABLE_PAGE_SIZE = 10;
const EMPTY_LIST = [];

function usePaginatedSlice(items, pageSize = TABLE_PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    setPage(1);
  }, [items]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const slice = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safePage, pageSize]);

  return { slice, page: safePage, setPage, totalPages, totalItems, pageSize };
}

function TablePagination({ page, totalPages, totalItems, pageSize, onPageChange }) {
  if (totalItems === 0 || totalItems <= pageSize) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  return (
    <div className="ra-pagination">
      <span className="ra-pagination-info">
        Showing {start.toLocaleString("en-IN")}–{end.toLocaleString("en-IN")} of{" "}
        {totalItems.toLocaleString("en-IN")}
      </span>
      <div className="ra-pagination-controls">
        <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          Previous
        </button>
        <span className="ra-pagination-page">
          Page {page} of {totalPages}
        </span>
        <button type="button" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
          Next
        </button>
      </div>
    </div>
  );
}

function downloadCsv(filename, rows) {
  const csv = rows.map((row) => row.map(toCsvSafe).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function RewardsAnalytics() {
  const { adminData } = useSelector((state) => ({
    adminData: state?.auth?.adminData,
  }));
  const hasEditPermission = hasModuleAction(adminData, "analytics", "edit");
  const hasExportPermission = hasModuleAction(adminData, "analytics", "export");

  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [city, setCity] = useState("All");
  const [profession, setProfession] = useState("All");
  const [coinValue, setCoinValue] = useState("0.01");
  const [search, setSearch] = useState("");
  const [popupSearch, setPopupSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsPopup, setUserDetailsPopup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboard, setDashboard] = useState(EMPTY_ANALYTICS_DASHBOARD);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError("");

    const filters = { startDate, endDate, city, profession };
    const payload = getWalletTransactionListPayload(filters);
    const response = await getWalletTransactionList(payload);
    setLoading(false);

    const mapped = mapWalletTransactionAnalyticsResponse(response, filters);
    if (mapped.error) {
      setError(mapped.error);
      toast.error(mapped.error);
      setDashboard(EMPTY_ANALYTICS_DASHBOARD);
      return;
    }

    setDashboard(mapped.data || EMPTY_ANALYTICS_DASHBOARD);
  }, [startDate, endDate, city, profession]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    setPopupSearch("");
  }, [userDetailsPopup]);

  const cities = useMemo(
    () => ["All", ...(dashboard.cities?.length ? dashboard.cities : [])],
    [dashboard.cities]
  );

  const professions = useMemo(
    () => ["All", ...(dashboard.professions?.length ? dashboard.professions : [])],
    [dashboard.professions]
  );

  const rewardEvents = dashboard.rewardEvents || [];

  const filteredUsers = dashboard.users || [];

  const sortedEventRows = useMemo(
    () => [...(dashboard.eventRows || [])].sort((a, b) => b.coinsIssued - a.coinsIssued),
    [dashboard.eventRows]
  );

  const drilldownUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    let users = filteredUsers;
    if (q) {
      users = filteredUsers.filter((user) => {
        const phoneDigits = String(user.phone || "").replace(/\D/g, "");
        const searchDigits = q.replace(/\D/g, "");
        const haystack =
          `${user.name} ${user.referralCode} ${user.city} ${user.profession} ${user.phone}`.toLowerCase();
        return (
          haystack.includes(q) ||
          (searchDigits.length > 0 && phoneDigits.includes(searchDigits))
        );
      });
    }
    return [...users].sort((a, b) => {
      const earnedA = Number(a.rewardsCoinsEarned || 0) + Number(a.referralCoinsEarned || 0);
      const earnedB = Number(b.rewardsCoinsEarned || 0) + Number(b.referralCoinsEarned || 0);
      return earnedB - earnedA;
    });
  }, [filteredUsers, search]);

  const eventPagination = usePaginatedSlice(sortedEventRows);
  const levelPagination = usePaginatedSlice(dashboard.levelRows ?? EMPTY_LIST);
  const drilldownPagination = usePaginatedSlice(drilldownUsers);

  const popupUsersList = useMemo(() => {
    const users = userDetailsPopup?.users ?? EMPTY_LIST;
    const q = popupSearch.trim().toLowerCase();
    const searchDigits = q.replace(/\D/g, "");

    let filtered = users;
    if (q) {
      filtered = users.filter((user) => {
        const phoneDigits = String(user.phone || "").replace(/\D/g, "");
        const haystack =
          `${user.name} ${user.phone} ${user.profession} ${user.city} ${user.qualification} ${user.specialisation}`.toLowerCase();
        return (
          haystack.includes(q) ||
          (searchDigits.length > 0 && phoneDigits.includes(searchDigits))
        );
      });
    }

    return [...filtered].sort(
      (a, b) => Number(b.coinsEarned || 0) - Number(a.coinsEarned || 0)
    );
  }, [userDetailsPopup?.users, popupSearch]);

  const popupUsersPagination = usePaginatedSlice(popupUsersList);
  const historyPagination = usePaginatedSlice(selectedUser?.transactions ?? EMPTY_LIST);

  const filterSummary = dashboard.filterSummary || null;

  const activeFilterLabels = useMemo(() => {
    const labels = [];
    if (city !== "All") labels.push(city);
    if (profession !== "All") labels.push(profession);
    if (startDate !== defaultRange.startDate || endDate !== defaultRange.endDate) {
      labels.push(`${startDate} to ${endDate}`);
    }
    return labels;
  }, [city, profession, startDate, endDate, defaultRange]);

  const hasNarrowingFilters = activeFilterLabels.length > 0;

  const resetFilters = () => {
    setCity("All");
    setProfession("All");
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
  };

  const metrics = dashboard.metrics || EMPTY_ANALYTICS_DASHBOARD.metrics;
  const referralFunnel = useMemo(
    () =>
      (dashboard.referralFunnel || []).filter(
        (step) => !/^invite shared$/i.test(String(step.step || "").trim())
      ),
    [dashboard.referralFunnel]
  );

  const eligibleUsersForPopup = useMemo(() => {
    const fromApi = dashboard.redemptionData?.eligibleUsers;
    if (Array.isArray(fromApi) && fromApi.length) return fromApi;

    return (dashboard.users || [])
      .filter((user) => user.redemptionEligible === true || getAvailableCoins(user) > 0)
      .map((user) => mapUserForPopup(user));
  }, [dashboard.redemptionData, dashboard.users]);

  const signUpsWithReferralUsersForPopup = useMemo(
    () => (dashboard.users || []).filter((user) => user.usedReferralCode).map((user) => mapUserForPopup(user)),
    [dashboard.users]
  );

  const signUpsWithoutReferralUsersForPopup = useMemo(
    () =>
      (dashboard.users || [])
        .filter((user) => user.signedUpWithoutReferral)
        .map((user) => mapUserForPopup(user)),
    [dashboard.users]
  );

  const redeemedUsersForPopup = useMemo(
    () =>
      (dashboard.users || [])
        .filter((user) => Number(user.coinsRedeemed || 0) > 0)
        .map((user) => mapUserForPopup(user, Number(user.coinsRedeemed || 0))),
    [dashboard.users]
  );

  const openEligibleUsersPopup = () => {
    setUserDetailsPopup({
      title: "User's Details",
      subtitle: "Users eligible to redeem",
      users: eligibleUsersForPopup,
    });
  };

  const openSignUpsWithReferralPopup = () => {
    setUserDetailsPopup({
      title: "User's Details",
      subtitle: "Sign-ups with referral code",
      users: signUpsWithReferralUsersForPopup,
    });
  };

  const openSignUpsWithoutReferralPopup = () => {
    setUserDetailsPopup({
      title: "User's Details",
      subtitle: "Sign-ups without referral code",
      users: signUpsWithoutReferralUsersForPopup,
    });
  };

  const openRedeemedUsersPopup = () => {
    setUserDetailsPopup({
      title: "User's Details",
      subtitle: "Users who redeemed coins",
      users: redeemedUsersForPopup,
    });
  };

  const openEventUsersPopup = (row) => {
    setUserDetailsPopup({
      title: "User's Details",
      subtitle: row.eventName ? `Users rewarded · ${row.eventName}` : "Users rewarded",
      users: Array.isArray(row.users) ? row.users : [],
    });
  };

  const exportDrilldown = () => {
    if (!hasExportPermission) {
      toast.error("Access Denied: You don't have permission to export data");
      return;
    }

    const header = [
      "User",
      "Referral Code",
      "Phone",
      "Email",
      "City",
      "Profession",
      "Specialisation",
      "Status",
      "Total Earned",
      "Redeemed",
      "Available",
      "Expired",
      "Reversed",
      "Rewards Coins",
      "Referral Coins",
      "L1 Coins",
      "L2 Coins",
      "L3 Coins",
      "L4 Coins",
      "L5 Coins",
      ...rewardEvents,
    ];

    const rows = drilldownUsers.map((user) => {
      const earned = user.rewardsCoinsEarned + user.referralCoinsEarned;
      const available = earned - user.coinsRedeemed - user.coinsExpired - user.coinsReversed;
      return [
        user.name,
        user.referralCode,
        user.phone,
        user.email,
        user.city,
        user.profession,
        user.specialisation,
        user.status,
        earned,
        user.coinsRedeemed,
        available,
        user.coinsExpired,
        user.coinsReversed,
        user.rewardsCoinsEarned,
        user.referralCoinsEarned,
        user.referralLevels.L1 || 0,
        user.referralLevels.L2 || 0,
        user.referralLevels.L3 || 0,
        user.referralLevels.L4 || 0,
        user.referralLevels.L5 || 0,
        ...rewardEvents.map((eventName) => user.rewardEvents[eventName] || 0),
      ];
    });

    downloadCsv("rewards-drilldown.csv", [header, ...rows]);
  };

  const exportUserHistory = (user) => {
    if (!hasExportPermission) {
      toast.error("Access Denied: You don't have permission to export data");
      return;
    }

    const rows = user.transactions.map((tx) => [tx.date, tx.type, tx.event, tx.coins, tx.status]);
    downloadCsv(
      `${user.name.toLowerCase().replace(/\s+/g, "-")}-transactions.csv`,
      [["Date", "Type", "Event", "Coins", "Status"], ...rows]
    );
  };

  const card = (title, value, sub, onClick) => (
    <div
      className={`ra-card${onClick ? " ra-clickable-card" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
    >
      <div className="ra-card-title">{title}</div>
      <div className="ra-card-value">{value}</div>
      {sub ? <div className="ra-card-sub">{sub}</div> : null}
    </div>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="rewards-analytics">
        <div className="ra-header">
          <h2>Rewards & Referral Analytics</h2>
          <p>Track rewards, referral conversion, wallet balances, and redemption performance in one view.</p>
        </div>

        <div className="ra-filters">
          <label>
            Start Date
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label>
            End Date
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <label>
            Work City
            <select value={city} onChange={(e) => setCity(e.target.value)}>
              {cities.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label>
            Profession
            <select value={profession} onChange={(e) => setProfession(e.target.value)}>
              {professions.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label>
            1 Coin Value (INR)
            <input
              type="number"
              min="0"
              step="0.01"
              value={coinValue}
              disabled={!hasEditPermission}
              title={!hasEditPermission ? "You don't have permission to edit" : undefined}
              onChange={(e) => {
                if (!hasEditPermission) return;
                setCoinValue(e.target.value);
              }}
            />
          </label>
        </div>

        {!loading && hasNarrowingFilters ? (
          <div className="ra-filter-status">
            <span>
              Filtered by <strong>{activeFilterLabels.join(" · ")}</strong> — showing{" "}
              <strong>{(dashboard.users || []).length.toLocaleString("en-IN")}</strong> users
              {filterSummary
                ? ` from ${filterSummary.matchedTransactions.toLocaleString(
                    "en-IN"
                  )} of ${filterSummary.totalTransactions.toLocaleString("en-IN")} transactions`
                : ""}
            </span>
            <button type="button" className="ra-filter-reset" onClick={resetFilters}>
              Reset filters
            </button>
          </div>
        ) : null}

        {loading ? <div className="ra-loading">Loading analytics…</div> : null}
        {!loading && error ? <div className="ra-error">{error}</div> : null}

        <div className="ra-grid ra-grid-4">
          {card("Coins Earned", metrics.totalCoinsEarned.toLocaleString("en-IN"), inr(metrics.totalCoinsEarned, coinValue))}
          {card(
            "Sign-Ups With Referral Code",
            metrics.signUpsWithReferral.toLocaleString("en-IN"),
            null,
            openSignUpsWithReferralPopup
          )}
          {card(
            "Sign-Ups Without Referral Code",
            metrics.signUpsWithoutReferral.toLocaleString("en-IN"),
            null,
            openSignUpsWithoutReferralPopup
          )}
          {card(
            "First Approved Shifts (Unique User Who Joined Via Referral Code)",
            (metrics.firstApprovedShifts ?? 0).toLocaleString("en-IN")
          )}
        </div>

        <section className="ra-section ra-wallet-section">
          <h3>Redemption & Wallet</h3>
          <p className="ra-note">Who can redeem now and what balance is still blocked or removed.</p>
          <div className="ra-wallet-grid">
            <div className="ra-mini-card ra-clickable-card" onClick={openEligibleUsersPopup} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && openEligibleUsersPopup()}>
              <div className="ra-mini-title">Users Eligible to Redeem</div>
              <div className="ra-mini-value">{metrics.usersEligibleToRedeem.toLocaleString("en-IN")}</div>
              <div className="ra-mini-desc">Users who currently meet redemption rules.</div>
            </div>
            <div className="ra-mini-card">
              <div className="ra-mini-title">Redeemable</div>
              <div className="ra-mini-value">{metrics.totalAvailable.toLocaleString("en-IN")}</div>
              <div className="ra-mini-pill">{inr(metrics.totalAvailable, coinValue)}</div>
              <div className="ra-mini-desc">Coins currently available for redemption.</div>
            </div>
            <div
              className="ra-mini-card ra-clickable-card"
              onClick={openRedeemedUsersPopup}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && openRedeemedUsersPopup()}
            >
              <div className="ra-mini-title">Already Redeemed Coins</div>
              <div className="ra-mini-value">{metrics.totalRedeemed.toLocaleString("en-IN")}</div>
              <div className="ra-mini-pill">{inr(metrics.totalRedeemed, coinValue)}</div>
              <div className="ra-mini-desc">Coins already redeemed by users.</div>
            </div>
            <div className="ra-mini-card">
              <div className="ra-mini-title">Expired</div>
              <div className="ra-mini-value">{metrics.totalExpired.toLocaleString("en-IN")}</div>
              <div className="ra-mini-pill">{inr(metrics.totalExpired, coinValue)}</div>
              <div className="ra-mini-desc">Coins removed after expiry.</div>
            </div>
            <div className="ra-mini-card">
              <div className="ra-mini-title">Blocked by Lock</div>
              <div className="ra-mini-value">{metrics.blockedByLock.toLocaleString("en-IN")}</div>
              <div className="ra-mini-desc">Users not yet eligible for redemption.</div>
            </div>
            <div className="ra-mini-card">
              <div className="ra-mini-title">Reversed</div>
              <div className="ra-mini-value">{metrics.totalReversed.toLocaleString("en-IN")}</div>
              <div className="ra-mini-pill">{inr(metrics.totalReversed, coinValue)}</div>
              <div className="ra-mini-desc">Coins removed after reward reversal or failed condition.</div>
            </div>
          </div>
        </section>

        <section className="ra-section ra-reward-performance-section">
          <h3>Rewards Performance</h3>
          <p className="ra-note">Summary excludes referral rewards and reflects event-based rewards only.</p>
          <div className="ra-reward-mini-grid">
            <div className="ra-mini-card">
              <div className="ra-mini-title">Rewards Coins Issued</div>
              <div className="ra-mini-value">{metrics.rewardsCoinsIssued.toLocaleString("en-IN")}</div>
              <div className="ra-mini-pill">{inr(metrics.rewardsCoinsIssued, coinValue)}</div>
              <div className="ra-mini-desc">Coins issued from rewards events excluding referral.</div>
            </div>
            <div className="ra-mini-card">
              <div className="ra-mini-title">Rewards Coins Expired</div>
              <div className="ra-mini-value">{metrics.rewardsCoinsExpired.toLocaleString("en-IN")}</div>
              <div className="ra-mini-pill">{inr(metrics.rewardsCoinsExpired, coinValue)}</div>
              <div className="ra-mini-desc">Reward coins expired in the selected date range.</div>
            </div>
          </div>
        </section>

        <section className="ra-section ra-event-performance-section">
          <h3>Rewards Event Performance</h3>
          <p className="ra-note">Shows which reward events are issuing the most coins and rewarding the most users.</p>
          <table className="ra-table ra-event-table">
            <thead>
              <tr>
                <th>Reward Event</th>
                <th>Coins Issued</th>
                <th>
                  <button type="button" className="ra-plain-click" onClick={() => {
                    const allUsers = [];
                    const usersById = new Map();
                    sortedEventRows.forEach((row) => {
                      (row.users || []).forEach((user) => {
                        if (!user?.id) return;
                        const existing = usersById.get(user.id);
                        if (existing) {
                          existing.coinsEarned =
                            Number(existing.coinsEarned || 0) + Number(user.coinsEarned || 0);
                          return;
                        }
                        usersById.set(user.id, { ...user });
                        allUsers.push(usersById.get(user.id));
                      });
                    });
                    setUserDetailsPopup({
                      title: "User's Details",
                      subtitle: "Users rewarded across all events",
                      users: allUsers,
                    });
                  }}>
                    Users Rewarded
                  </button>
                </th>
                <th>Avg Coins / User</th>
              </tr>
            </thead>
            <tbody>
              {sortedEventRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="ra-table-empty">
                    No reward event data available for the selected filters.
                  </td>
                </tr>
              ) : (
                eventPagination.slice.map((row) => (
                  <tr key={row.eventName}>
                    <td className="ra-event-name-cell">{row.eventName}</td>
                    <td>
                      <div className="ra-event-coins">{row.coinsIssued.toLocaleString("en-IN")}</div>
                      <div className="ra-event-inr">{inr(row.coinsIssued, coinValue)}</div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ra-plain-click"
                        onClick={() => openEventUsersPopup(row)}
                      >
                        {row.usersRewarded}
                      </button>
                    </td>
                    <td>{row.avg}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <TablePagination
            page={eventPagination.page}
            totalPages={eventPagination.totalPages}
            totalItems={eventPagination.totalItems}
            pageSize={eventPagination.pageSize}
            onPageChange={eventPagination.setPage}
          />
        </section>

        <section className="ra-section ra-funnel-section">
          <h3>Referral Funnel</h3>
          <p className="ra-note">Tracks referral journey from invite to sign-up, activation, reward, and redemption.</p>
          <div className="ra-funnel-list">
            {referralFunnel.length === 0 ? (
              <div className="ra-table-empty">No referral funnel data available for the selected filters.</div>
            ) : (
              referralFunnel.map((step) => {
                const sharePct = Number(step.share.replace("%", ""));
                const widthPct = Math.max(sharePct, 3);
                return (
                  <div
                    className="ra-funnel-step"
                    key={step.step}
                    style={{ "--funnel-width": `${widthPct}%` }}
                  >
                    <div className="ra-funnel-step-top">
                      <div>
                        <div className="ra-funnel-name">{step.step}</div>
                        {step.coins != null ? (
                          <div className="ra-funnel-count">{Number(step.coins).toLocaleString("en-IN")}</div>
                        ) : (
                          <div className="ra-funnel-count">{step.count.toLocaleString("en-IN")}</div>
                        )}
                      </div>
                    </div>
                    <div className="ra-funnel-step-mid">
                      <div className="ra-funnel-shape-track">
                        <div className="ra-funnel-shape-fill" />
                      </div>
                    </div>
                    <div className="ra-funnel-step-right">
                      <div className="ra-funnel-rate">{step.conversion}</div>
                      <div className="ra-funnel-base">{sharePct.toFixed(1)}% of invites</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="ra-section ra-table-card">
          <h3>Referral Coin Split</h3>
          <p className="ra-note">Breakdown of referral coins across direct and network referral levels.</p>
          <div className="ra-compact-kpi-grid ra-compact-kpi-grid-3">
            <div className="ra-compact-kpi">
              <div className="ra-compact-kpi-label">Referral Coins Issued</div>
              <div className="ra-compact-kpi-value">{metrics.referralIssued.toLocaleString("en-IN")}</div>
              <div className="ra-compact-kpi-inr">{inr(metrics.referralIssued, coinValue)}</div>
              <div className="ra-compact-kpi-sub">Total coins issued through referral rewards.</div>
            </div>
            <div className="ra-compact-kpi">
              <div className="ra-compact-kpi-label">Invite Shared Via User On App</div>
              <div className="ra-compact-kpi-value">
                {(metrics.inviteSharedViaApp ?? 0).toLocaleString("en-IN")}
              </div>
              <div className="ra-compact-kpi-sub">
                Unique users who shared a referral invite from the app.
              </div>
            </div>
            <div className="ra-compact-kpi">
              <div className="ra-compact-kpi-label">Referral Coins Expired</div>
              <div className="ra-compact-kpi-value">{metrics.referralExpired.toLocaleString("en-IN")}</div>
              <div className="ra-compact-kpi-inr">{inr(metrics.referralExpired, coinValue)}</div>
              <div className="ra-compact-kpi-sub">Referral coins expired in the selected date range.</div>
            </div>
          </div>
        </section>

        <section className="ra-section ra-table-card">
          <h3>Referral Level Performance</h3>
          <p className="ra-note">Shows how each referral level from L1 to L5 is performing.</p>
          <div className="ra-table-wrap">
            <table className="ra-table ra-insight-table">
              <thead>
                <tr>
                  <th>Referral Level</th>
                  <th>People Joined</th>
                  <th>Coins Issued</th>
                  <th>Avg Coins / Join</th>
                </tr>
              </thead>
              <tbody>
                {levelPagination.totalItems === 0 ? (
                  <tr>
                    <td colSpan={4} className="ra-table-empty">
                      No referral level data available for the selected filters.
                    </td>
                  </tr>
                ) : (
                  levelPagination.slice.map((row) => (
                    <tr key={row.level}>
                      <td>
                        <span className="ra-cell-strong">{row.level}</span>
                      </td>
                      <td>{row.peopleJoined.toLocaleString("en-IN")}</td>
                      <td className="ra-coin-stat">
                        {row.coinsIssued.toLocaleString("en-IN")}
                        <span className="ra-table-inr">{inr(row.coinsIssued, coinValue)}</span>
                      </td>
                      <td>{Number(row.avg).toFixed(1)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={levelPagination.page}
            totalPages={levelPagination.totalPages}
            totalItems={levelPagination.totalItems}
            pageSize={levelPagination.pageSize}
            onPageChange={levelPagination.setPage}
          />
        </section>

        <section className="ra-section ra-table-card ra-drilldown-section">
          <h3>User-Level Drill-Down</h3>
          <p className="ra-note">User-wise wallet summary with transaction history and export.</p>
          <div className="ra-table-toolbar">
            <input
              type="text"
              className="ra-table-search"
              placeholder="Search user, code, city, phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="button" className="ra-action-btn" onClick={exportDrilldown}>
              Export
            </button>
          </div>
          <div className="ra-table-wrap">
            <table className="ra-table ra-insight-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Phone No.</th>
                  <th>City / Profession</th>
                  <th>Coins Earned</th>
                  <th>Coins Redeemed</th>
                  <th>Coins Available</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {drilldownUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="ra-table-empty">
                      No matching users for the selected filters.
                    </td>
                  </tr>
                ) : (
                  drilldownPagination.slice.map((user) => {
                    const earned = user.rewardsCoinsEarned + user.referralCoinsEarned;
                    const available = earned - user.coinsRedeemed - user.coinsExpired - user.coinsReversed;
                    return (
                      <tr key={user.id}>
                        <td>
                          <span className="ra-cell-strong">{user.name}</span>
                          <span className="ra-cell-sub">Code: {user.referralCode}</span>
                        </td>
                        <td>
                          <span className="ra-cell-strong">{user.phone || "—"}</span>
                        </td>
                        <td>
                          <span className="ra-cell-strong">{user.city || "—"}</span>
                          <span className="ra-cell-sub">{user.profession || "—"}</span>
                        </td>
                        <td>
                          <div className="ra-coin-cell">
                            <span className="ra-coin-stat">{earned.toLocaleString("en-IN")}</span>
                            <span className="ra-table-inr-inline">{inr(earned, coinValue)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="ra-coin-cell">
                            <span className="ra-coin-stat subtle">{user.coinsRedeemed.toLocaleString("en-IN")}</span>
                            <span className="ra-table-inr-inline">{inr(user.coinsRedeemed, coinValue)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="ra-coin-cell">
                            <span className="ra-coin-stat">{available.toLocaleString("en-IN")}</span>
                            <span className="ra-table-inr-inline">{inr(available, coinValue)}</span>
                          </div>
                        </td>
                        <td>
                          <button type="button" className="ra-action-btn" onClick={() => setSelectedUser(user)}>
                            View History
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={drilldownPagination.page}
            totalPages={drilldownPagination.totalPages}
            totalItems={drilldownPagination.totalItems}
            pageSize={drilldownPagination.pageSize}
            onPageChange={drilldownPagination.setPage}
          />
        </section>

        {selectedUser ? (
          <div className="ra-modal-backdrop open" onClick={() => setSelectedUser(null)}>
            <div className="ra-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ra-modal-head">
                <div>
                  <div className="ra-modal-title">{selectedUser.name}</div>
                  <div className="ra-modal-subtitle">
                    {selectedUser.city || "—"} · {selectedUser.profession || "—"} · Referral Code{" "}
                    {selectedUser.referralCode}
                  </div>
                </div>
                <button type="button" className="ra-action-btn" onClick={() => setSelectedUser(null)}>
                  Close
                </button>
              </div>
              <div className="ra-modal-body">
                <div className="ra-modal-grid">
                  {[
                    ["Coins Earned", selectedUser.rewardsCoinsEarned + selectedUser.referralCoinsEarned],
                    ["Coins Redeemed", selectedUser.coinsRedeemed],
                    [
                      "Coins Available",
                      selectedUser.rewardsCoinsEarned +
                        selectedUser.referralCoinsEarned -
                        selectedUser.coinsRedeemed -
                        selectedUser.coinsExpired -
                        selectedUser.coinsReversed,
                    ],
                  ].map(([label, value]) => (
                    <div className="ra-modal-stat" key={label}>
                      <div className="ra-modal-stat-label">{label}</div>
                      <div className="ra-modal-stat-value">{Number(value).toLocaleString("en-IN")}</div>
                      <div className="ra-modal-stat-subvalue">{inr(value, coinValue)}</div>
                    </div>
                  ))}
                </div>
                <div className="ra-table-wrap" style={{ marginTop: 0 }}>
                  <table className="ra-table ra-insight-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Event</th>
                        <th>Coins</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedUser.transactions.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="ra-table-empty">
                            No transaction history available yet.
                          </td>
                        </tr>
                      ) : (
                        historyPagination.slice.map((tx, idx) => (
                          <tr key={`${selectedUser.id}-${idx}`}>
                            <td>{tx.date}</td>
                            <td>
                              <span
                                className={`ra-history-type ${
                                  tx.type === "Credit" ? "ra-history-credit" : "ra-history-debit"
                                }`}
                              >
                                {tx.type}
                              </span>
                            </td>
                            <td>{tx.event}</td>
                            <td>
                              {tx.coins.toLocaleString("en-IN")}
                              <span className="ra-table-inr-inline">{inr(tx.coins, coinValue)}</span>
                            </td>
                            <td>{tx.status}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <TablePagination
                  page={historyPagination.page}
                  totalPages={historyPagination.totalPages}
                  totalItems={historyPagination.totalItems}
                  pageSize={historyPagination.pageSize}
                  onPageChange={historyPagination.setPage}
                />
              </div>
              <div className="ra-modal-foot">
                <div className="ra-modal-foot-note">Export this user transaction history as CSV.</div>
                <button
                  type="button"
                  className="ra-action-btn"
                  onClick={() => exportUserHistory(selectedUser)}
                >
                  Export History
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {userDetailsPopup ? (
          <div className="ra-modal-backdrop open" onClick={() => setUserDetailsPopup(null)}>
            <div className="ra-modal ra-modal-wide" onClick={(e) => e.stopPropagation()}>
              <div className="ra-modal-head">
                <div>
                  <div className="ra-modal-title">{userDetailsPopup.title}</div>
                  <div className="ra-modal-subtitle">
                    {userDetailsPopup.subtitle}
                    {popupUsersList.length
                      ? ` · ${popupUsersList.length.toLocaleString("en-IN")} user${
                          popupUsersList.length === 1 ? "" : "s"
                        }`
                      : ""}
                  </div>
                </div>
                <button type="button" className="ra-action-btn" onClick={() => setUserDetailsPopup(null)}>
                  Close
                </button>
              </div>
              <div className="ra-modal-body">
                <div className="ra-table-toolbar ra-modal-table-toolbar">
                  <input
                    type="text"
                    className="ra-table-search"
                    placeholder="Search name or phone number"
                    value={popupSearch}
                    onChange={(e) => setPopupSearch(e.target.value)}
                  />
                </div>
                <div className="ra-table-wrap" style={{ marginTop: 0 }}>
                  <table className="ra-table ra-insight-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Profession</th>
                        <th>Qualification</th>
                        <th>Specialisation</th>
                        <th>City</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!popupUsersList.length ? (
                        <tr>
                          <td colSpan={6} className="ra-table-empty">
                            No user details available.
                          </td>
                        </tr>
                      ) : (
                        popupUsersPagination.slice.map((user, idx) => (
                          <tr key={user.id || `${user.name}-${idx}`}>
                            <td>{user.name || "—"}</td>
                            <td>{user.phone || "—"}</td>
                            <td>{user.profession || "—"}</td>
                            <td>{user.qualification || "—"}</td>
                            <td>{user.specialisation || "—"}</td>
                            <td>{user.city || "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <TablePagination
                  page={popupUsersPagination.page}
                  totalPages={popupUsersPagination.totalPages}
                  totalItems={popupUsersPagination.totalItems}
                  pageSize={popupUsersPagination.pageSize}
                  onPageChange={popupUsersPagination.setPage}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}

export default RewardsAnalytics;
